import React, { useState } from 'react';
import { SiteHookup, StoreHookupType, SiteHookupCreate, HOOKUP_ICONS, HOOKUP_LABELS } from '../../../types/site';
import { siteService } from '../../../services/siteService';

interface SiteHookupSelectorProps {
  siteId: number;
  hookups: SiteHookup[];
  hookupTypes: StoreHookupType[];
  onChange: (hookups: SiteHookup[]) => void;
}

const SiteHookupSelector: React.FC<SiteHookupSelectorProps> = ({
  siteId,
  hookups,
  hookupTypes,
  onChange
}) => {
  const [saving, setSaving] = useState<number | null>(null);

  const getHookupIcon = (hookupType: string): string => {
    return HOOKUP_ICONS[hookupType as keyof typeof HOOKUP_ICONS] || '⚙️';
  };

  const getHookupLabel = (hookupType: string): string => {
    return HOOKUP_LABELS[hookupType as keyof typeof HOOKUP_LABELS] || hookupType;
  };

  const isHookupSelected = (hookupTypeId: number): boolean => {
    return hookups.some(h => h.store_hookup_type_id === hookupTypeId);
  };

  const getExistingHookup = (hookupTypeId: number): SiteHookup | undefined => {
    return hookups.find(h => h.store_hookup_type_id === hookupTypeId);
  };

  const handleToggleHookup = async (hookupType: StoreHookupType) => {
    const existing = getExistingHookup(hookupType.id);

    try {
      setSaving(hookupType.id);

      if (existing) {
        // Remove hookup
        await siteService.removeSiteHookup(existing.id);
        onChange(hookups.filter(h => h.id !== existing.id));
      } else {
        // Add hookup
        const hookupData: SiteHookupCreate = {
          store_hookup_type_id: hookupType.id,
          amp: hookupType.hookup_type === 'electricity' ? hookupType.default_amp : undefined,
          available: true
        };
        const newHookup = await siteService.addSiteHookup(siteId, hookupData);
        onChange([...hookups, newHookup]);
      }
    } catch (err) {
      console.error('Error toggling hookup:', err);
    } finally {
      setSaving(null);
    }
  };

  const activeHookupTypes = hookupTypes.filter(h => h.active);

  // Common hookup types for "Select All Common" feature
  const COMMON_HOOKUP_TYPES = ['water', 'sewer', 'electricity'];

  const handleSelectAllCommon = async () => {
    const commonTypes = activeHookupTypes.filter(h =>
      COMMON_HOOKUP_TYPES.includes(h.hookup_type) && !isHookupSelected(h.id)
    );

    if (commonTypes.length === 0) {
      return; // All common hookups already selected
    }

    for (const hookupType of commonTypes) {
      await handleToggleHookup(hookupType);
    }
  };

  // Check if all common hookups are already selected
  const allCommonSelected = activeHookupTypes
    .filter(h => COMMON_HOOKUP_TYPES.includes(h.hookup_type))
    .every(h => isHookupSelected(h.id));

  // Check if there are any common hookups available
  const hasCommonHookups = activeHookupTypes.some(h =>
    COMMON_HOOKUP_TYPES.includes(h.hookup_type)
  );

  return (
    <div className="site-hookup-selector">
      <div className="hookup-selector-header">
        <p className="form-hint">
          Select the hookups available at this site. Click to add or remove.
        </p>
        {hasCommonHookups && !allCommonSelected && (
          <button
            className="btn btn-secondary btn-sm"
            onClick={handleSelectAllCommon}
            disabled={saving !== null}
          >
            Select Full Hookup (W/S/E)
          </button>
        )}
      </div>

      <div className="hookup-grid">
        {activeHookupTypes.map(hookupType => {
          const isSelected = isHookupSelected(hookupType.id);
          const isSaving = saving === hookupType.id;

          return (
            <div
              key={hookupType.id}
              className={`hookup-card ${isSelected ? 'selected' : ''} ${isSaving ? 'saving' : ''}`}
              onClick={() => !isSaving && handleToggleHookup(hookupType)}
            >
              <div className="hookup-card-main">
                <span className="hookup-card-icon">
                  {getHookupIcon(hookupType.hookup_type)}
                </span>
                <span className="hookup-card-label">
                  {hookupType.display_name || getHookupLabel(hookupType.hookup_type)}
                </span>
                <span className="hookup-card-check">
                  {isSelected ? '✓' : ''}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {activeHookupTypes.length === 0 && (
        <div className="no-hookup-types">
          <p>No hookup types configured for this store.</p>
          <p>Add hookup types in the Store Setup section first.</p>
        </div>
      )}

      {/* Summary */}
      {hookups.length > 0 && (
        <div className="hookup-summary">
          <strong>Selected Hookups:</strong>
          <div className="hookup-summary-icons">
            {hookups.map(hookup => {
              const hookupType = hookupTypes.find(h => h.id === hookup.store_hookup_type_id);
              if (!hookupType) return null;
              return (
                <span key={hookup.id} className="hookup-summary-item" title={hookupType.display_name}>
                  {getHookupIcon(hookupType.hookup_type)}
                  {hookupType.hookup_type === 'electricity' && hookup.amp && (
                    <span className="amp-badge">{hookup.amp}A</span>
                  )}
                </span>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default SiteHookupSelector;
